import { authorizedGetRequest, postRequest, authorizedPutRequest } from "./httpClient";

// New Agent Config version-control API surface (CONTRACT.md section 9 - "Agent Config Version
// Control" cross-repo project). Distinct from agentConfigApi.js/toolDefinitionsApi.js (the
// existing, per-practice "one bundle" draft/submit/publish systems for prompt and tools
// separately) - this targets the backend's newer /api/agents/:practiceId/:direction/* endpoints,
// which version ALL seven config parts (instructions, model, voice, speech, behavior, tools,
// postCall) together as one release, per agent (practiceId + direction, "inbound"/"outbound").
// Both existing systems keep working untouched; this is additive, same coexistence pattern
// toolDefinitionsApi.js's own header comment describes for itself vs /api-integration.
export const createAgentReleaseApi = ({ user }) => {
  const resolveIds = (practiceId, direction) => {
    const resolvedPracticeId = Number.parseInt(practiceId, 10);
    if (!resolvedPracticeId) {
      throw new Error("PracticeId is required");
    }
    if (direction !== "inbound" && direction !== "outbound") {
      throw new Error('direction must be "inbound" or "outbound"');
    }
    return resolvedPracticeId;
  };

  const getAgentSummary = async (practiceId, direction) => {
    const pid = resolveIds(practiceId, direction);
    return authorizedGetRequest(user.token, `/api/agents/${pid}/${direction}`);
  };

  const getAgentDraft = async (practiceId, direction) => {
    const pid = resolveIds(practiceId, direction);
    return authorizedGetRequest(user.token, `/api/agents/${pid}/${direction}/draft`);
  };

  // revision: the draft Revision this client last saw (null if it has never seen a draft for this
  // agent) - required by the backend (PUT .../draft requires it) for its compare-and-swap check.
  const saveAgentDraft = async (practiceId, direction, body, revision) => {
    const pid = resolveIds(practiceId, direction);
    return authorizedPutRequest(user.token, `/api/agents/${pid}/${direction}/draft`, {
      body,
      revision: revision ?? null,
    });
  };

  const submitAgentDraft = async (practiceId, direction) => {
    const pid = resolveIds(practiceId, direction);
    return postRequest(user.token, `/api/agents/${pid}/${direction}/draft/submit`, {});
  };

  const publishAgentDraft = async (practiceId, direction, { draftRevision, expectedRouteRevision, reason } = {}) => {
    const pid = resolveIds(practiceId, direction);
    return postRequest(user.token, `/api/agents/${pid}/${direction}/publish`, {
      draftRevision,
      expectedRouteRevision,
      reason: reason ?? null,
    });
  };

  // partName: one of model | voice | speech | behavior | tools | postCall (never "instructions" -
  // that goes through the draft/submit/publish path above).
  const saveAgentPart = async (practiceId, direction, partName, { value, expectedRouteRevision, reason } = {}) => {
    const pid = resolveIds(practiceId, direction);
    return authorizedPutRequest(user.token, `/api/agents/${pid}/${direction}/parts/${partName}`, {
      value,
      expectedRouteRevision,
      reason: reason ?? null,
    });
  };

  const rollbackAgentRelease = async (practiceId, direction, { toReleaseId, expectedRouteRevision, reason }) => {
    const pid = resolveIds(practiceId, direction);
    return postRequest(user.token, `/api/agents/${pid}/${direction}/rollback`, {
      toReleaseId,
      expectedRouteRevision,
      reason,
    });
  };

  const getAgentReleases = async (practiceId, direction, { limit, offset } = {}) => {
    const pid = resolveIds(practiceId, direction);
    return authorizedGetRequest(user.token, `/api/agents/${pid}/${direction}/releases`, { limit, offset });
  };

  const getAgentRelease = async (practiceId, direction, releaseId) => {
    const pid = resolveIds(practiceId, direction);
    return authorizedGetRequest(user.token, `/api/agents/${pid}/${direction}/releases/${releaseId}`);
  };

  const getAgentDeployments = async (practiceId, direction, { limit, offset } = {}) => {
    const pid = resolveIds(practiceId, direction);
    return authorizedGetRequest(user.token, `/api/agents/${pid}/${direction}/deployments`, { limit, offset });
  };

  // from/to: each a release id (number/numeric string), or the literal "live" | "draft" | "candidate".
  const getAgentDiff = async (practiceId, direction, from, to) => {
    const pid = resolveIds(practiceId, direction);
    return authorizedGetRequest(user.token, `/api/agents/${pid}/${direction}/diff`, { from, to });
  };

  const getCallRelease = async (callSid) => {
    if (!callSid) {
      throw new Error("callSid is required");
    }
    return authorizedGetRequest(user.token, `/api/calls/${encodeURIComponent(callSid)}/release`);
  };

  return {
    getAgentSummary,
    getAgentDraft,
    saveAgentDraft,
    submitAgentDraft,
    publishAgentDraft,
    saveAgentPart,
    rollbackAgentRelease,
    getAgentReleases,
    getAgentRelease,
    getAgentDeployments,
    getAgentDiff,
    getCallRelease,
  };
};
